// Semáforo de Establecimientos — vista de la Dirección de Comercio (+ admin).
// Lee el padrón gobernado (mcp_django apps/establishments) vía /django-api; el
// org sale del JWT. Gateada por requireRoles:['comercio'] en el registry + el
// scope establishments:read en el backend (candado real).
//
// 1,288 establecimientos: la búsqueda y la paginación son del SERVIDOR (antes
// traía 500 y filtraba client-side → 788 invisibles y conteos sobre una muestra).
// Los KPIs vienen de `counts` (todo el padrón que cumple la búsqueda).

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, RefreshCw, Search, Store, X } from 'lucide-react';
import { listSemaforo, type SemaforoPage as SemaforoPageData, type SemaforoRow } from './api';
import { OsApp, osActionBtn } from '@/modules/orion-shell/OsApp';
import { Sheet } from '@/components/ui/Sheet';

// ── Etiquetas / colores (tokens de tema; nada de bg-white crudo) ──────

const DIMS: { key: string; label: string }[] = [
  { key: 'proteccion_civil', label: 'P. Civil' },
  { key: 'ecologia', label: 'Ecología' },
  { key: 'via_publica', label: 'Vía Pública' },
  { key: 'uso_suelo', label: 'Uso Suelo' },
  { key: 'bomberos', label: 'Bomberos' },
  { key: 'gobernacion', label: 'Gobernación' },
];

const DIM_STYLE: Record<string, { label: string; cls: string }> = {
  cumple: { label: 'En regla', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  en_tramite: { label: 'En trámite', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  no_liberado: { label: 'No liberado', cls: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30' },
  no_cumple: { label: 'No cumple', cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30' },
  no_aplica: { label: '—', cls: 'bg-muted/60 text-muted-foreground/70 border-border' },
};

const SEMAFORO_STYLE: Record<string, { label: string; dot: string; cls: string }> = {
  verde: { label: 'En regla', dot: 'bg-emerald-500', cls: 'text-emerald-700 dark:text-emerald-300' },
  amarillo: { label: 'En proceso', dot: 'bg-amber-500', cls: 'text-amber-700 dark:text-amber-300' },
  rojo: { label: 'En regularización', dot: 'bg-rose-500', cls: 'text-rose-700 dark:text-rose-300' },
};

const PAGE_SIZE = 50;

function dimBadge(value: string) {
  const s = DIM_STYLE[value] ?? DIM_STYLE.no_aplica;
  return (
    <span className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-medium leading-none ${s.cls}`}>
      {s.label}
    </span>
  );
}

type Filtro = 'todos' | 'verde' | 'amarillo' | 'rojo';
const EMPTY: SemaforoPageData = { total: 0, counts: {}, items: [] };

export function SemaforoPage() {
  const [data, setData] = useState<SemaforoPageData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [busca, setBusca] = useState('');        // lo que teclea el usuario
  const [q, setQ] = useState('');                // búsqueda "asentada" (debounce) → servidor
  const [offset, setOffset] = useState(0);
  // Detalle de un establecimiento (lista → detalle; en móvil a pantalla completa).
  const [sel, setSel] = useState<SemaforoRow | null>(null);

  // Debounce del cuadro de búsqueda: no pegamos al backend en cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setQ(busca.trim()), 300);
    return () => clearTimeout(t);
  }, [busca]);

  // Cambiar búsqueda o filtro vuelve a la primera página.
  useEffect(() => { setOffset(0); }, [q, filtro]);

  // Evita pisar una respuesta vieja con una nueva (peticiones concurrentes).
  const reqId = useRef(0);
  const load = useCallback(async () => {
    const mine = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const d = await listSemaforo({
        q: q || undefined,
        estatus: filtro === 'todos' ? undefined : filtro,
        limit: PAGE_SIZE,
        offset,
      });
      if (mine === reqId.current) setData(d);
    } catch (e) {
      if (mine === reqId.current) setError(e instanceof Error ? e.message : 'No se pudo cargar el padrón.');
    } finally {
      if (mine === reqId.current) setLoading(false);
    }
  }, [q, filtro, offset]);

  useEffect(() => { void load(); }, [load]);

  const counts = {
    verde: data.counts.verde ?? 0,
    amarillo: data.counts.amarillo ?? 0,
    rojo: data.counts.rojo ?? 0,
  };
  const totalBusqueda = counts.verde + counts.amarillo + counts.rojo;   // todo el q (KPIs)
  const total = data.total;                                             // q + estatus (paginador)
  const desde = total === 0 ? 0 : offset + 1;
  const hasta = Math.min(offset + PAGE_SIZE, total);
  const pagina = Math.floor(offset / PAGE_SIZE) + 1;
  const paginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const kpi = (key: Filtro, label: string, n: number, dot: string) => (
    <button
      type="button"
      onClick={() => setFiltro((f) => (f === key ? 'todos' : key))}
      className={`flex flex-1 items-center gap-3 rounded-lg border bg-card px-4 py-3 text-left transition hover:border-[var(--acc1)]/40 ${
        filtro === key ? 'border-[var(--acc1)]/60 ring-1 ring-[var(--acc1)]/40' : 'border-border'
      }`}
    >
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
      <span className="flex flex-col">
        <span className="text-2xl font-bold tabular-nums text-foreground">{n}</span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </span>
    </button>
  );

  const nombreDe = (r: SemaforoRow) =>
    r.nombre_comercial?.trim() ? r.nombre_comercial : 'Sin nombre registrado';

  return (
    <OsApp
      icon={Store}
      title="Padrón de establecimientos"
      subtitle="Semáforo de licencias y funcionamiento del padrón oficial"
      actions={
        <button type="button" onClick={() => void load()} disabled={loading} className={osActionBtn}>
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} aria-hidden /> Actualizar
        </button>
      }
    >
    <div className="flex flex-col gap-5">
      {/* KPIs / filtros por semáforo (cuentan TODO el padrón que cumple la búsqueda) */}
      <div className="flex flex-wrap gap-3">
        {kpi('verde', 'En regla', counts.verde, 'bg-emerald-500')}
        {kpi('amarillo', 'En proceso', counts.amarillo, 'bg-amber-500')}
        {kpi('rojo', 'En regularización', counts.rojo, 'bg-rose-500')}
        <div className="flex flex-1 items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3">
          <span className="flex flex-col">
            <span className="text-2xl font-bold tabular-nums text-foreground">{totalBusqueda}</span>
            <span className="text-xs text-muted-foreground">{q ? 'Coinciden' : 'Total en padrón'}</span>
          </span>
        </div>
      </div>

      {/* Búsqueda (servidor) */}
      <div className="relative">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nombre, giro o domicilio…"
          className="w-full rounded-2xl border border-border bg-card py-2 pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus:border-[var(--acc1)] focus:outline-none"
        />
        {busca && (
          <button
            type="button"
            onClick={() => setBusca('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
            aria-label="Limpiar búsqueda"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {data.partial && (
        <div role="status" className="rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-800 dark:text-amber-200">
          El servidor todavía no pagina el padrón: se muestran los primeros 500 registros y los conteos son de esos 500.
        </div>
      )}

      {/* Estados */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-700 dark:text-rose-200">
          <AlertTriangle size={16} className="shrink-0" />
          {error}
        </div>
      )}

      {loading && data.items.length === 0 && (
        <div className="flex items-center justify-center gap-2 p-16 text-muted-foreground">
          <RefreshCw size={16} className="animate-spin" /> Cargando padrón…
        </div>
      )}

      {!loading && !error && data.items.length === 0 && (
        <div className="rounded-2xl border border-border bg-card p-16 text-center text-muted-foreground">
          {q || filtro !== 'todos'
            ? 'Ningún establecimiento coincide con la búsqueda.'
            : 'Aún no hay establecimientos en el padrón.'}
        </div>
      )}

      {/* Móvil / tableta: tarjetas (sin scroll horizontal) → detalle */}
      {data.items.length > 0 && (
        <ul className="flex flex-col gap-2 lg:hidden" aria-label="Establecimientos">
          {data.items.map((r) => {
            const s = SEMAFORO_STYLE[r.semaforo as string] ?? SEMAFORO_STYLE.rojo;
            const sinNombre = !r.nombre_comercial?.trim();
            return (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => setSel(r)}
                  className="flex w-full items-start gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-left hover:bg-muted/40"
                >
                  <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${s.dot}`} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate font-medium ${sinNombre ? 'italic text-muted-foreground' : 'text-foreground'}`}>{nombreDe(r)}</span>
                    <span className="block truncate text-xs text-muted-foreground">{[r.giro, r.domicilio].filter(Boolean).join(' · ') || '—'}</span>
                  </span>
                  <span className={`shrink-0 whitespace-nowrap text-xs font-semibold ${s.cls}`}>{s.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Escritorio: tabla */}
      {data.items.length > 0 && (
        <div className="hidden overflow-x-auto rounded-2xl border border-border lg:block">
          <table className="w-full min-w-[820px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Establecimiento</th>
                <th className="px-3 py-2 font-medium">Semáforo</th>
                {DIMS.map((d) => (
                  <th key={d.key} className="px-3 py-2 text-center font-medium">{d.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => {
                const s = SEMAFORO_STYLE[r.semaforo as string] ?? SEMAFORO_STYLE.rojo;
                const sinNombre = !r.nombre_comercial?.trim();
                return (
                  <tr key={r.id} onClick={() => setSel(r)} className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-muted/30">
                    <td className="px-3 py-2.5">
                      <div className={`font-medium ${sinNombre ? 'italic text-muted-foreground' : 'text-foreground'}`}>
                        {nombreDe(r)}
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-1" title={[r.giro, r.domicilio].filter(Boolean).join(' · ')}>
                        {[r.giro, r.domicilio].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold ${s.cls}`}>
                        <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                        {s.label}
                      </span>
                    </td>
                    {DIMS.map((d) => (
                      <td key={d.key} className="px-3 py-2.5 text-center">
                        {dimBadge(String(r.dimensiones?.[d.key] ?? 'no_aplica'))}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginador */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            {desde}–{hasta} de {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              disabled={offset === 0 || loading}
              className="flex items-center gap-1 rounded-2xl border border-border bg-card px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
            >
              <ChevronLeft size={15} /> Anterior
            </button>
            <span className="text-xs tabular-nums text-muted-foreground">{pagina} / {paginas}</span>
            <button
              type="button"
              onClick={() => setOffset((o) => (o + PAGE_SIZE < total ? o + PAGE_SIZE : o))}
              disabled={hasta >= total || loading}
              className="flex items-center gap-1 rounded-2xl border border-border bg-card px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
            >
              Siguiente <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {total > 0 && <>Mostrando {desde}–{hasta} de {total}. </>}
        El estatus lo mantiene la dependencia responsable del padrón; el agente consulta este
        mismo padrón para responder a la ciudadanía.
      </p>
      {sel && (
        <Sheet open inShell onClose={() => setSel(null)} label={nombreDe(sel)} title={nombreDe(sel)}>
          {(() => {
            const s = SEMAFORO_STYLE[sel.semaforo as string] ?? SEMAFORO_STYLE.rojo;
            return (
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-sm font-semibold ${s.cls}`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} aria-hidden /> {s.label}
                  </span>
                  {sel.estatus_app && <span className="text-xs text-muted-foreground">Estatus: {sel.estatus_app}</span>}
                </div>
                <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-border p-3">
                    <dt className="text-xs text-muted-foreground">Giro</dt>
                    <dd className="text-sm font-medium">{sel.giro || '—'}</dd>
                  </div>
                  <div className="rounded-2xl border border-border p-3">
                    <dt className="text-xs text-muted-foreground">Domicilio</dt>
                    <dd className="text-sm font-medium">{sel.domicilio || '—'}</dd>
                  </div>
                </dl>
                <div>
                  <h3 className="mb-2 text-sm font-semibold">Dictámenes</h3>
                  <ul className="divide-y divide-border rounded-2xl border border-border">
                    {DIMS.map((d) => (
                      <li key={d.key} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                        <span>{d.label}</span>
                        {dimBadge(String(sel.dimensiones?.[d.key] ?? 'no_aplica'))}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()}
        </Sheet>
      )}
    </div>
    </OsApp>
  );
}
